import React, { useState } from "react";
import "./HardwareDetails.scss";
import {
  CheckCircleIcon,
  ArrowBackIcon,
} from "../../../../../shared/margincalculator/components/icons/Icon";

interface Device {
  id: string;
  name: string;
  specs: string;
  price: number;
}

interface HardwareDetailsProps {
  device: Device;
  onBack: () => void;
  onConfirm: (selection: DeviceSelection) => void;
}

export interface DeviceSelection {
  deviceId: string;
  amortization: "once" | "monthly";
  term: 12 | 24 | 36;
  monthlyRate: number;
}

const HardwareDetails: React.FC<HardwareDetailsProps> = ({
  device,
  onBack,
  onConfirm,
}) => {
  const [amortization, setAmortization] = useState<"once" | "monthly">(
    "monthly",
  );
  const [term, setTerm] = useState<12 | 24 | 36>(24);

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);

  const monthlyRate = parseFloat((device.price / term).toFixed(2));

  return (
    <div className="hardware-details">
      <button className="hardware-details__back-btn" onClick={onBack}>
        <ArrowBackIcon /> Zurück zur Geräteauswahl
      </button>

      <div className="hardware-details__card">
        <header className="hardware-details__header">
          <div className="hardware-details__title-group">
            <CheckCircleIcon className="hardware-details__status-icon" />
            <div>
              <h3>
                {device.name} {device.specs}
              </h3>
              <p>EK (netto): {formatPrice(device.price)}</p>
            </div>
          </div>
          <div className="hardware-details__price-display">
            <span className="hardware-details__price-label">EINMALIG</span>
            <span className="hardware-details__price-value">
              {formatPrice(device.price)}
            </span>
          </div>
        </header>

        <section className="hardware-details__section">
          <label className="hardware-details__label">AMORTISATION</label>
          <div className="hardware-details__toggle-group">
            <button
              className={`hardware-details__toggle ${amortization === "once" ? "hardware-details__toggle--active" : ""}`}
              onClick={() => setAmortization("once")}
            >
              Einmalig
            </button>
            <button
              className={`hardware-details__toggle ${amortization === "monthly" ? "hardware-details__toggle--active" : ""}`}
              onClick={() => setAmortization("monthly")}
            >
              Im Monatspreis
            </button>
          </div>
        </section>

        {amortization === "monthly" && (
          <>
            <section className="hardware-details__section">
              <label className="hardware-details__label">LAUFZEIT</label>
              <div className="hardware-details__toggle-group">
                {[12, 24, 36].map((t) => (
                  <button
                    key={t}
                    className={`hardware-details__toggle ${term === t ? "hardware-details__toggle--active" : ""}`}
                    onClick={() => setTerm(t as 12 | 24 | 36)}
                  >
                    {t} Monate
                  </button>
                ))}
              </div>
            </section>

            <section className="hardware-details__rate-box">
              <div className="hardware-details__rate-info">
                <strong>Monatsrate</strong>
                <span>
                  {formatPrice(device.price)} auf {term} Monate verteilt
                </span>
              </div>
              <div className="hardware-details__rate-value">
                <strong>+{formatPrice(monthlyRate)}</strong>
                <span>pro Monat</span>
              </div>
            </section>
          </>
        )}

        <button
          className="hardware-details__confirm-btn"
          onClick={() =>
            onConfirm({ deviceId: device.id, amortization, term, monthlyRate })
          }
        >
          <CheckCircleIcon size={20} /> Hardware übernehmen
        </button>
      </div>
    </div>
  );
};

export default HardwareDetails;
